<template>
  <div :class="$style.container" v-if="hasLineActivationProvider != null">
    <SettingsPagePanel>
      <DefaultIntroductionMessageView
        @doneSave="handleDoneSave"
        @onBeforeLeaveConfirmation="emit('onBeforeLeaveConfirmation', $event)"
        :introductionMessageContent="defaultIntroductionMessage"
        :hasLineActivationProvider="hasLineActivationProvider"
      />
    </SettingsPagePanel>
    <SettingsPagePanel v-if="hasLineActivationProvider">
      <ConditionalIntroductionMessageView
        :conditionalIntroductionMessages="conditionalIntroductionMessages"
      />
    </SettingsPagePanel>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import ConditionalIntroductionMessageView from './ConditionalIntroductionMessageView.vue'
import DefaultIntroductionMessageView from './DefaultIntroductionMessageView.vue'
import { useFetchSettingsPageIntroductionMessageViewQuery } from './SettingsPageIntroductionMessageView.generated'

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: {
      onConfirm: () => void
      onAbort: () => void
    }
  ): void
}>()

const { result, refetch } = useFetchSettingsPageIntroductionMessageViewQuery({
  fetchPolicy: 'no-cache',
})

const handleDoneSave = () => refetch()

const hasLineActivationProvider = computed(() => {
  return result.value?.viewer.hasLineActivationProvider
})

const defaultIntroductionMessage = computed(() => {
  if (!result.value) {
    return undefined
  }
  return result.value.introductionMessageSetting.default?.content
})

const conditionalIntroductionMessages = computed(() => {
  if (!result.value) {
    return undefined
  }
  return result.value?.introductionMessageSetting.conditional.map(c => ({
    guid: c.guid,
    content: c.content ?? {
      __typename: 'PlainIntroductionMessageContent',
      body: '',
      selections: [],
    },
    condition: {
      guid: c.condition?.userRegistrationInflowSourceDefinition.guid ?? '',
      inflowSourceName:
        c.condition?.userRegistrationInflowSourceDefinition.inflowSourceName ??
        '',
    },
  }))
})

const route = useRoute('introductionMessageSetting')
const entranceGUID = computed(() => route.params.entranceGUID)
watch(
  entranceGUID,
  () => {
    refetch()
  },
  { immediate: true }
)
</script>

<style lang="scss" module>
.container {
  display: flex;
  flex-direction: column;
  gap: 32px;
}
</style>
